import type { UUID } from '@appcues/domain-types';
import type { Action } from 'ext/lib/collections';
import { createCollectionReducer, DELETED } from 'ext/lib/collections';
import type { LanguageCode } from 'ext/lib/localization';
import { isLanguageCode } from 'ext/lib/localization';
import {
  TYPE,
  SET_LOCALE,
  REMOVE_DRAFT_LOCALE,
  CLEAR_DRAFT_LOCALES,
} from './actions';
import type { LocalesState, Locale, LocalesCollection } from './types';

const collectionReducer = createCollectionReducer(TYPE);

const initialState: LocalesState = {
  active: null,
  draftLocales: [],
};

export default function reducer(
  state: LocalesState = initialState,
  action: Action
): LocalesState {
  if (action.type === SET_LOCALE) {
    return {
      ...state,
      active: action.payload.id,
      draftLocales: [...(state?.draftLocales ?? []), action.payload.id],
    };
  }

  if (action.type === REMOVE_DRAFT_LOCALE) {
    return {
      ...state,
      draftLocales: (state.draftLocales ?? []).filter(
        id => id !== action.payload.id
      ),
    };
  }

  if (action.type === CLEAR_DRAFT_LOCALES) {
    return {
      ...state,
      draftLocales: [],
    };
  }

  return collectionReducer(state as any, action) as LocalesState;
}

/**
 * Extracts the primary language code from a locale's conditions.
 *
 * Locale conditions store language codes in the format:
 * { conditions: { and: [{ properties: { value: 'en\nfr\nes' } }] } }
 *
 * The value field may contain multiple language codes separated by newlines,
 * representing a list of languages the locale targets. This function returns
 * the first (primary) language code if it's a valid ISO language code.
 *
 * @param value - The locale object to extract the language code from
 * @returns The primary language code if valid, or null if:
 *   - The locale is null/undefined
 *   - The conditions structure is malformed
 *   - The extracted code is not a valid language code
 */
export const getLanguageCode = (
  value: Locale | null | undefined
): LanguageCode | null => {
  // Guard against null/undefined locale
  if (!value) {
    return null;
  }

  // Validate conditions structure exists
  const { conditions } = value;
  if (!conditions || typeof conditions !== 'object') {
    return null;
  }

  // Validate 'and' array exists and has at least one element
  const andConditions = conditions.and;
  if (!Array.isArray(andConditions) || andConditions.length === 0) {
    return null;
  }

  // Validate first condition has properties with a value
  const firstCondition = andConditions[0];
  if (
    !firstCondition ||
    !firstCondition.properties ||
    typeof firstCondition.properties.value !== 'string'
  ) {
    return null;
  }

  // Extract the first language code (before any newline) and normalize
  // to lowercase — LANGUAGES uses lowercase IDs (e.g. 'fr-fr') but
  // custom locale properties may store BCP 47 casing (e.g. 'fr-FR')
  const extractedCode = firstCondition.properties.value
    .split('\n')[0]
    ?.toLowerCase();

  if (!extractedCode || !isLanguageCode(extractedCode)) {
    return null;
  }

  return extractedCode;
};

export const selectLocales = <T extends { locales: LocalesState }>(
  state: T
): LocalesCollection => {
  const entriesWithAddedLangCode = Object.entries(state[TYPE]).map(
    ([key, value]) => {
      if (key === 'active' || key === 'draftLocales') return [key, value];
      return [
        key,
        {
          ...(value as Locale),
          languageCode: getLanguageCode(value as Locale),
        },
      ];
    }
  );

  return Object.fromEntries(
    entriesWithAddedLangCode.filter(
      ([key, locale]) =>
        key !== 'active' &&
        key !== 'draftLocales' &&
        !(locale as any)[DELETED] &&
        (locale as any)?.name
    )
  ) as LocalesCollection;
};

export const selectLocale = <T extends { locales: LocalesState }>(
  state: T,
  id: UUID
): Locale | undefined => {
  const locale = state[TYPE][id] as Locale | undefined;
  if (!locale) return undefined;

  return {
    ...locale,
    languageCode: getLanguageCode(locale),
  };
};

export const selectActiveLocale = <T extends { locales: LocalesState }>(
  state: T
): Locale | undefined => {
  const activeId = state[TYPE].active;
  return activeId ? selectLocale(state, activeId) : undefined;
};

export const selectDraftLocales = <T extends { locales: LocalesState }>(
  state: T
): UUID[] => state[TYPE].draftLocales;
